import { ll, lt } from "@liftquest/shared/localized";
import type { TrainingData as ApiTraining } from "@/lib/api";

type ApiExercise = ApiTraining["items"][number]["exercise"];

export type ExerciseView = {
  id: string;
  name: string;
  type: ApiExercise["type"];
  muscles: string[];
  equipment: string;
  videoUrl: string | null;
  instructions: string[];
  setup: string;
  weightStep: number;
};

export function toExerciseView(e: ApiExercise, locale: string): ExerciseView {
  return {
    id: e.id,
    name: lt(e.name, locale),
    type: e.type,
    muscles: e.muscles,
    equipment: e.equipment,
    videoUrl: e.videoUrl,
    instructions: ll(e.instructions, locale),
    setup: lt(e.setup, locale),
    weightStep: e.weightStep,
  };
}

/** Training data with all texts resolved to the current language. */
export function toTrainingView(d: ApiTraining, locale: string) {
  return {
    session: { ...d.session, name: lt(d.session.name, locale) },
    phase: d.phase ? { rir: d.phase.rir, goal: lt(d.phase.goal, locale) } : null,
    warmup: ll(d.warmup, locale),
    settings: d.settings,
    items: d.items.map((it) => ({
      ...it,
      original: toExerciseView(it.original, locale),
      exercise: toExerciseView(it.exercise, locale),
      alternative: it.alternative ? toExerciseView(it.alternative, locale) : null,
      alternativeNote: lt(it.alternativeNote, locale),
      notes: lt(it.notes, locale),
    })),
    logged: d.logged,
    library: d.library
      .map((e) => ({ ...e, name: lt(e.name, locale) }))
      .sort((a, b) => a.name.localeCompare(b.name, locale)),
  };
}

export type TrainingData = ReturnType<typeof toTrainingView>;
export type TrainingItem = TrainingData["items"][number];
export type LoggedSet = TrainingData["logged"][number];
